import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, EnvironmentProviders, importProvidersFrom, inject, isDevMode, LOCALE_ID, provideAppInitializer, Provider, provideZoneChangeDetection } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withDebugTracing, withDisabledInitialNavigation, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { I18nService, provideTranslation, TranslateService } from 'i18n';
import { browserLocale, ENVIRONMENT, Logger, LOGO, Platform, Theme, ThemeService } from 'shared';

import { FormsModule } from '@angular/forms';
import { DropdownModule, SidebarModule } from '@coreui/angular-pro';
import { IconSetService } from '@coreui/icons-angular';
import { MATOMO_CONFIGURATION, NgxMatomoTrackerModule } from '@ngx-matomo/tracker';
import { CalendarModule } from 'calendar';

import { appInfoInterceptor, authInterceptor, BootstrapService, provideCookieConsent, corsInterceptor, FeatureFlagsService, impersonationInterceptor, maintenanceInterceptor, MenuService, noCacheInterceptor, serverUrlInterceptor } from 'app';
import { MenuService as CredoMenuService } from './services/menu.service';

import { BrowserModule } from '@angular/platform-browser';
import { Clipboard } from '@ionic-native/clipboard/ngx';
import { Deeplinks } from '@ionic-native/deeplinks/ngx';
import { Device } from '@ionic-native/device/ngx';
import { FileOpener } from '@ionic-native/file-opener/ngx';
import { File } from '@ionic-native/file/ngx';
import { InAppBrowser } from '@ionic-native/in-app-browser/ngx';
import { ScreenOrientation } from '@ionic-native/screen-orientation/ngx';
import { SecureStorageEcho } from '@ionic-native/secure-storage-echo/ngx';
import { StatusBar } from '@ionic-native/status-bar/ngx';
import { ConsentAppConfig, MatomoService, TRACKING_CHECKOUT_CONFIG, TrackingCheckoutConfig } from 'analytics';
import { firstValueFrom, lastValueFrom } from 'rxjs';

function createAppInitializer(defaultTheme?: Theme): () => Promise<any> {
  return async (): Promise<any> => {
    const bootstrapService: BootstrapService = inject(BootstrapService);
    const translateService: TranslateService = inject(TranslateService);
    const i18nService: I18nService = inject(I18nService);
    const platform: Platform = inject(Platform);
    const featureFlagsService: FeatureFlagsService = inject(FeatureFlagsService);
    const matomoService: MatomoService | null = inject(MatomoService, { optional: true });
    const themeService = inject(ThemeService)
    const environment: any = inject(ENVIRONMENT);
    const swPromise = platform.is('hybrid') && 'serviceWorker' in navigator 
                    ? navigator.serviceWorker
                        .getRegistrations()
                        .then(registrations => Promise.all(registrations?.map(r => r.unregister())))
                    : Promise.resolve()
    try {
      return await swPromise;
    } finally {
      await initializeFeatureFlags(featureFlagsService);
      if (matomoService) {
        initializeMatomo(matomoService, environment);
      }
      // return await lastValueFrom(concat(
      //   bootstrapService.bootstrap(),
      //   translateService.load(i18nService.getLanguage())
      // ));
  
      await lastValueFrom(bootstrapService.bootstrap());
      const availableLanguages = bootstrapService.getSetting('languages');
      const defaultLanguage = bootstrapService.getSetting('defaultLanguage') ;
      if (availableLanguages) {
        translateService.setAvailableLangs(availableLanguages);
      }
      if (defaultLanguage) {
        translateService.setDefaultLang(defaultLanguage);
      }
      // The active language may have been resolved from the browser locale / a
      // stored preference (e.g. 'de') before availableLangs was known. Now that
      // we know the app's configured languages, clamp it: never activate a
      // language this app doesn't offer (it would have no translations and render
      // raw keys). Falls back to the configured default, then the first available.
      let activeLang = i18nService.getLanguage();
      if (availableLanguages?.length && !availableLanguages.includes(activeLang)) {
        activeLang = (defaultLanguage && availableLanguages.includes(defaultLanguage))
          ? defaultLanguage
          : availableLanguages[0];
      }
      i18nService.setLanguage(activeLang, false); // sync transloco active lang + storage
      await lastValueFrom(translateService.load(activeLang));
  
      const theme: Theme | undefined = bootstrapService.getSetting('theme') || defaultTheme;
      themeService.applyTheme(theme);
    }
  }
}

async function initializeFeatureFlags(featureFlagsService: FeatureFlagsService) {
  await firstValueFrom(featureFlagsService.loadFeatureFlags());
  featureFlagsService.startPolling(60_000);
}

function initializeMatomo(matomoService: MatomoService, environment: any) {
  matomoService.initialize(environment.matomo);
  const consent = localStorage.getItem('matomo-consent');
  if (consent === 'granted') {
    matomoService.rememberConsentGiven();
  } else if (environment.matomo.requireConsent) {
    matomoService.requireConsent();
  }
}

export interface AppInstanceConfig {
  routes: any[];  
  providers: Array<Provider | EnvironmentProviders>;
  translationScope: string,
  environment: any;
  theme: Theme,
  logo: any;
  /**
   * Cookie banner + preference center of the Tracking Engine V2
   * (docs/tracking/EVENT_CONTRACT_V2.md). Off unless an app opts in with
   * `{ enabled: true, pageId: '<brand>-app' }`.
   */
  consent: ConsentAppConfig;
  /**
   * Tracking Engine reports of the shared screens. `checkout` opts the order wizard
   * into `checkout_started` / `checkout_step` under the app's own page id
   * (e.g. `{ checkout: { pageId: 'f3-checkout', plans: { CARD_PLATINUM: 'platinum' } } }`).
   * Without it the wizard sends nothing.
   */
  tracking: { checkout?: TrackingCheckoutConfig };
}

export function createAppConfig(config: Partial<AppInstanceConfig> = {}): ApplicationConfig {
  const {
    routes = [],
    providers = [],
    translationScope,
    environment,
    theme,
    logo,
    consent = { enabled: false },
    tracking = {},
  } = config;

  const matomoEnabled = environment.matomo !== false;
  const matomoConfig = typeof environment.matomo === 'object' ? environment.matomo : {};

  const result: Array<Provider | EnvironmentProviders> = [
    // provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
      withInMemoryScrolling({ scrollPositionRestoration: 'top', anchorScrolling: 'enabled' }),
      withDisabledInitialNavigation(),
      ...(Logger.isDebugEnabled() ? [withDebugTracing()] : [])
    ),

    provideHttpClient(withInterceptors([
      serverUrlInterceptor,
      authInterceptor,
      impersonationInterceptor,
      appInfoInterceptor,
      corsInterceptor,
      noCacheInterceptor,
      maintenanceInterceptor
    ])),

    // provideTranslation({ scope: "credo" }),
    provideTranslation({ scope: ['credo', translationScope].filter(scope=>!!scope).join(',')}),

    importProvidersFrom(
      SidebarModule,
      DropdownModule,
      BrowserModule,
      FormsModule,
      CalendarModule,
      ...(matomoEnabled ? [NgxMatomoTrackerModule] : [])
    ),

    provideAnimationsAsync(),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:10000'
    }),

    IconSetService,

    SecureStorageEcho, 
    ScreenOrientation, 
    StatusBar,
    Deeplinks, 
    File, 
    FileOpener, 
    Device, 
    InAppBrowser,
    Clipboard,

    { provide: MenuService, useClass: CredoMenuService },

    {
      provide: LOCALE_ID,
      useFactory: () => localStorage.getItem('locale') || browserLocale() || 'en-US'
    },

    ...(environment ? [{ provide: ENVIRONMENT, useValue: environment }] : []),
    // ...(logo ? [{ provide: LOGO, useValue: logo }] : []),
    { provide: LOGO, useFactory: () => {
      const bootstrapService = inject(BootstrapService);
      return bootstrapService.getSetting('logo') || logo ||  [];
    }},

    // `matomo.enabled: false` in an app's environment must switch Matomo off
    // COMPLETELY (customer decision 2026-10-10: Matomo retired in favour of the
    // first-party WolfPack Tracking Engine). ngx-matomo only honours its own
    // `disabled` flag — without it the module still injected matomo.js and
    // tracked the initial page view, whatever MatomoService.isEnabled() said.
    // The module and MatomoTracker stay provided so the existing
    // MatomoTrackerService call sites keep resolving; they are no-ops when
    // disabled.
    ...(matomoEnabled ? [{
      provide: MATOMO_CONFIGURATION,
      useValue: {
        siteId: environment?.matomo?.siteId || 1,
        trackerUrl: environment?.matomo?.trackerUrl || '',
        scriptUrl: environment?.matomo?.scriptUrl,
        requireConsent: environment?.matomo?.requireConsent,
        enableLinkTracking: true,
        trackAppInitialLoad: environment?.matomo?.enabled !== false,
        ...matomoConfig,
        disabled: environment?.matomo?.enabled === false
      }
    }] : []),

    // NOTE: the following provideAppInitializer calls lead to bootstrap hanging 
    // of angular app (ios and desktop are performing as expected). 
    // As a workaroung we have combined all of them in a single function.
    // provideAppInitializer(initializeFeatureFlags),
    provideAppInitializer(createAppInitializer(theme)),

    ...provideCookieConsent(consent),
    ...(tracking.checkout ? [{ provide: TRACKING_CHECKOUT_CONFIG, useValue: tracking.checkout }] : []),
    // ...(environment.matomo.enabled ? [provideAppInitializer(initializeMatomo)] : []),

    ...providers
  ];

  return { providers: result };
}